import { Build, Component, Element, Prop, State, h } from '@stencil/core';
import { v4 } from 'uuid';
import type { LtcInputTextCustomEvent, LtcInputZipCustomEvent } from '~/components';
import type { ICarpentryComponent, IColorTheme, THomeLoanType } from '~/interfaces';
import { type IWidgetTracking, WidgetClicked, WidgetLoaded, WidgetUsed, callUniqueEntry } from '~/util/analytics';
import { HomeLoanType } from '~/util/constants';
import { MaxLength } from '~/util/decorators';
import { parseAndAddUrlParams } from '~/util/fetch';

const HEADER_LIMIT = 55 as const;
const CTA_LIMIT = 25 as const;
const FOOTER_LIMIT = 35 as const;

const FOOTER_TEXT = 'No impact to your credit score' as const;
const CTA_TEXT = 'Get Your Rates' as const;
const HEADER_TEXT_MORTGAGE = 'Compare mortgage rates from top lenders in minutes' as const;
const HEADER_TEXT_REFINANCE = 'Compare mortgage refinance rates from top lenders in minutes' as const;
const HEADER_TEXT_EQUITY = 'Compare home equity loan rates from top lenders in minutes' as const;

const MIN_AMOUNT = 0 as const;

@Component({
  tag: 'ltc-lead-home',
  styleUrl: 'home-loan-leadbox.less',
  shadow: true,
})
export class HomeLoanLeadbox implements ICarpentryComponent {
  /*
   * 1. Own Properties
   */
  id = v4();

  priceEl?: HTMLLtcInputTextElement;

  /*
   * 2. Reference to host HTML element.
   */
  @Element() host: HTMLLtcLeadHomeElement;

  /*
   * 3. State() variables
   */
  @State() loanTypeValue: THomeLoanType;
  @State() amountValue: number;
  @State() zipCode: string;
  @State() zipIsValidating = false;

  /*
   * 4. Public Property API
   */

  /**
   * If `true` will render header as H1-like style (DM-Serif), otherwise H3-like (Lato).
   */
  @Prop({ reflect: true }) useHeadlineFont: boolean;

  /**
   * Leadbox color theme
   */
  @Prop({ reflect: true }) theme: IColorTheme = 'white';

  /**
   * Default input shown is Purchase Price/Home Value. Set this to `true` to use Down Payment/Remaining Balance input filed.
   */
  @Prop({ reflect: true }) useSecondaryInput: boolean;

  /**
   * Set `true` to hide Loan Type dropdown select
   */
  @Prop({ reflect: true, mutable: true }) hideLoanTypeSelect: boolean;

  /**
   * Default value for Loan Type dropdown select
   */
  @Prop({ reflect: true }) loanType: THomeLoanType = 'mortgage';

  /**
   * Leadbox header. Set to override default value
   */
  @MaxLength(HEADER_LIMIT)
  @Prop({ reflect: true })
  header: string;

  /**
   * Use this setting to override default footer icon.
   * For available icons see the `ltc-icon` documentation.
   */
  @Prop({ reflect: true }) footerIcon: string;

  /**
   * Use this setting to override default footer text
   */
  @MaxLength(FOOTER_LIMIT)
  @Prop({ reflect: true })
  footerText: string;

  /**
   * Use this setting to override default CTA button text
   */
  @MaxLength(CTA_LIMIT)
  @Prop({ reflect: true })
  ctaText: string;

  /**
   * If `true`, will hide the footer on the Leadbox
   */
  @Prop({ reflect: true }) hideFooter: boolean;

  /**
   * If `true`, open the CTA action in a new browser window, if not (default) - in the same window.
   */
  @Prop({ reflect: true }) newWindow: boolean;

  /**
   * Any extra parameters that you want to add to a splitter-id URL for CTA button.
   * <br/>
   * Example: `LoanType=mortgage&AndMore=SomethingElse`
   */
  @Prop({ reflect: true }) withQueryParam: string;

  /**
   * Set `true` to add ZIP input filed
   */
  @Prop({ reflect: true }) showZipInput: boolean;

  /*
   * Prop lifecycle events
   */

  /*
   * 5. Events section
   */

  /*
   * 6. Component lifecycle events
   */
  constructor() {
    this.loanTypeValue = this.loanType;
  }

  componentDidLoad(): void {
    WidgetLoaded(this, this.getTrackingProps());
  }

  componentWillLoad(): void {
    if (Build.isServer) {
      return;
    }
  }

  /*
   * 7. Listeners
   */

  /*
   * 8. Public methods API
   */

  /*
   * 9. Local methods
   */
  callTrackingOnce() {
    callUniqueEntry(this.id, () => WidgetUsed(this, this.getTrackingProps()));
  }

  private getHeaderText = () => {
    if (this.header) {
      return this.header;
    }

    switch (this.loanTypeValue) {
      case 'mortgage':
        return HEADER_TEXT_MORTGAGE;
      case 'refinance':
        return HEADER_TEXT_REFINANCE;
      case 'home-equity':
        return HEADER_TEXT_EQUITY;
    }
  };

  private getCtaText = () => {
    return this.ctaText || CTA_TEXT;
  };

  private getFooterText = () => {
    return this.footerText || FOOTER_TEXT;
  };

  private getFooterIcon = () => {
    return this.footerIcon || 'security';
  };

  private getLoanLabel = (): string => {
    switch (this.loanTypeValue) {
      case 'mortgage':
        return this.useSecondaryInput ? 'Down Payment' : 'Purchase Price';
      case 'refinance':
      case 'home-equity':
        return this.useSecondaryInput ? 'Remaining Balance' : 'Home Value';
    }
  };

  private setPriceInputRef = (el?: HTMLLtcInputTextElement) => {
    this.priceEl = el;
  };

  private onPriceChange = (e: LtcInputTextCustomEvent<string>) => {
    this.callTrackingOnce();

    if (Number.isFinite(Number(e.detail))) {
      this.amountValue = Number(e.detail);
      this.priceEl?.setValue(e.detail);
    }
  };

  private onPurposeChange = (e: Event) => {
    this.callTrackingOnce();

    this.loanTypeValue = (e.target as HTMLSelectElement).value as THomeLoanType;
  };

  private getSplitterId = () => {
    switch (this.loanTypeValue) {
      case 'mortgage':
        return 'wp-mortgage';
      case 'refinance':
        return 'wp-refinance';
      case 'home-equity':
        return 'wp-home-equity';
    }
  };

  private redirectToSplitter = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();

    const url = new URL(REPLACE_LT_WWW_SPLITTER_URL);

    url.searchParams.set('id', this.getSplitterId());

    if (this.showZipInput && this.zipCode?.length === 5) {
      url.searchParams.set('PropertyAddressZipCode', this.zipCode);
    }

    //PurchasePrice
    let amountValueParam = '';

    switch (this.loanTypeValue) {
      case 'mortgage':
        amountValueParam = this.useSecondaryInput ? 'DownPayment' : 'PurchasePrice';
        break;
      case 'refinance':
      case 'home-equity':
        amountValueParam = this.useSecondaryInput ? 'FirstMortgageBalance' : 'EstimatedPropertyValue';
        break;
    }

    if (this.useSecondaryInput) {
      url.searchParams.set(amountValueParam, String(this.amountValue));
    }

    parseAndAddUrlParams(url, this.withQueryParam);

    WidgetClicked(this, this.getTrackingProps());

    window.open(url, this.newWindow ? '_blank' : '_self');
  };

  getTrackingProps = (): IWidgetTracking => {
    const inputs = this.showZipInput ? ['zip'] : [];

    if (!this.hideLoanTypeSelect) {
      inputs.push('loanType');
    }

    switch (this.loanTypeValue) {
      case 'mortgage':
        this.useSecondaryInput ? inputs.push('downPayment') : inputs.push('purchasePrice');
        break;
      case 'refinance':
      case 'home-equity':
        this.useSecondaryInput ? inputs.push('remainingBalance') : inputs.push('homeValue');
        break;
    }

    return {
      footer_displayed: !this.hideFooter,
      footer_icon: this.getFooterIcon(),
      footer_text: this.getFooterText(),
      headline_tag: this.useHeadlineFont ? 'h1' : 'h3',
      headline_text: this.getHeaderText(),
      inputs_included: inputs.join(','),
      theme: this.theme,
      extra_query_param: this.withQueryParam,
    };
  };

  private onLtZipValidating = ({ detail }: LtcInputZipCustomEvent<boolean>) => {
    this.zipIsValidating = detail;
  };

  private onLtZipChange = ({ detail }: LtcInputZipCustomEvent<string>) => {
    if (this.zipCode) {
      this.callTrackingOnce();
    }
    this.zipCode = detail;
  };

  /*
   * 10. render() function
   */
  render() {
    const { amountValue, hideFooter, hideLoanTypeSelect, theme, useHeadlineFont, zipIsValidating, showZipInput } = this;

    return (
      <article class={{ container: true, [`theme-${theme}`]: true }}>
        <header class={{ h1: useHeadlineFont, h3: !useHeadlineFont }}>{this.getHeaderText()}</header>
        <form
          onSubmit={(e) => e.preventDefault()}
          class={{
            'form-container': true,
            'no-loan-select': hideLoanTypeSelect,
            'no-zip-input': !showZipInput,
          }}
        >
          {hideLoanTypeSelect ? null : (
            <label class={{ 'select-with-label': true, 'select-with-label--empty': !this.loanTypeValue }}>
              <span>Loan Type</span>
              <select name="LoanType" onInput={this.onPurposeChange}>
                <option value="" disabled selected={!this.loanTypeValue}>
                  Loan Type
                </option>
                {HomeLoanType.map(({ label, value }) => {
                  return (
                    // color is for Chrome on Windows specific
                    <option style={{ color: '#101f30' }} key={value} value={value} selected={value === this.loanTypeValue}>
                      {label}
                    </option>
                  );
                })}
              </select>
              <ltc-icon name="chevron-down" aria-label="Select" />
            </label>
          )}
          <ltc-input-text
            ref={this.setPriceInputRef}
            label={this.getLoanLabel()}
            class="loan-amount"
            value={amountValue ? String(amountValue) : undefined}
            required={true}
            inputmode="numeric"
            iMaskOptions={{
              mask: Number,
              scale: 0,
              thousandsSeparator: ',',
              normalizeZeros: true,
              radix: '.',
              min: MIN_AMOUNT,
              autofix: true,
            }}
            onLtcChange={this.onPriceChange}
          >
            <ltc-icon name="dollar-sign" aria-label="Dollar Sign" slot="leftIcon" />
          </ltc-input-text>
          {showZipInput ? <ltc-input-zip class="zip-code" onLtChange={this.onLtZipChange} onLtValidating={this.onLtZipValidating} /> : null}
          <button
            type="button"
            tabindex="0"
            class="cta-btn"
            disabled={zipIsValidating}
            onClick={this.redirectToSplitter}
            onKeyPress={this.redirectToSplitter}
          >
            {this.getCtaText()}
          </button>
        </form>
        {hideFooter ? null : (
          <footer>
            <ltc-icon name={this.getFooterIcon()} aria-label="Footer Icon" size="small" />
            &nbsp;{this.getFooterText()}
          </footer>
        )}
      </article>
    );
  }
}
